import React, { useState } from 'react'
import styled from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { Bell, CheckCircle, Clock, LogOut, Shield, UserMinus, UserPlus, Users } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import {
  NotificationRow,
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation
} from 'state/services/community/community'
import { useSession } from 'views/Community/components/useSession'

const Wrapper = styled.div`
  position: relative;
`

const BellButton = styled.button`
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
`

/*
 * Red, and it has to be red.
 *
 * Every other accent on this platform is the brand cyan, which is exactly why
 * a count of things waiting for you cannot be: it would read as decoration.
 * The one place we deliberately break the palette is the one place a person
 * must notice without looking for it.
 */
const Badge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #e5484d;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
  border: 2px solid ${({ theme }) => theme.colors.background};
  box-sizing: content-box;
`

const Panel = styled(Flex)`
  position: absolute;
  right: 0;
  top: 44px;
  z-index: 40;
  width: 320px;
  max-height: 60vh;
  overflow-y: auto;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);

  ${({ theme }) => theme.mediaQueries.tablet} {
    position: fixed;
    left: 12px;
    right: 12px;
    width: auto;
  }
`

const Item = styled.button<{ $unread: boolean }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ theme, $unread }) => ($unread ? theme.colors.gray9 : 'transparent')};
  color: ${({ theme }) => theme.colors.white};
`

const ItemIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.gray6};
`

const FooterLink = styled.button`
  width: 100%;
  margin-top: 6px;
  padding: 10px;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.gray8};
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.cyan};
`

const Muted = styled(Text)`
  opacity: 0.6;
`

const Head = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 4px 8px;
`

const LinkButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.cyan};
`

/**
 * What each kind says, rendered here rather than stored as a sentence.
 *
 * The server records a kind and the ids it refers to; the words live in the
 * client. A message composed on the server would be frozen in the wording of
 * the day it was sent and in one language, and would still be sitting in
 * somebody's bell a year after we changed how we say it.
 */
/**
 * The icon for each kind, from the set the rest of the application uses.
 *
 * react-feather is already the header's icon library — stroke-drawn, one
 * weight, inheriting `currentColor` — so notifications look like the rest of
 * the portal rather than like a phone keyboard. An emoji renders in whatever
 * the operating system feels like and cannot take the theme's colour at all,
 * which is why the bell that shipped as 🔔 was wrong on every count.
 */
export function iconFor(kind: string): React.ReactNode {
  const size = 16
  switch (kind) {
    case 'clan.invited':
      return <UserPlus size={size} />
    case 'clan.invite.accepted':
    case 'clan.join.approved':
      return <CheckCircle size={size} />
    case 'clan.invite.declined':
    case 'clan.join.rejected':
      return <UserMinus size={size} />
    case 'clan.join.requested':
      return <Users size={size} />
    case 'clan.member.left':
      return <LogOut size={size} />
    case 'clan.member.removed':
      return <UserMinus size={size} />
    case 'clan.leadership.transferred':
      return <Shield size={size} />
    default:
      return <Clock size={size} />
  }
}

/**
 * Where a notification takes you — which is not the same as where it is about.
 *
 * The comment this replaces said "everything the bell can carry today is about
 * clans, and the clans panel is where an invitation is answered", and then sent
 * every row to `/community`, whose default tab is Users. A leader who tapped
 * "somebody asked to join" landed on a list of people with no way to see the
 * request. The destination has to be the screen where the reader can do the
 * thing the notification is telling them about.
 */
export function hrefFor(n: NotificationRow): string {
  const tag = typeof n.data?.clanTag === 'string' ? n.data.clanTag : null
  switch (n.kind) {
    // Answered from the clans panel: the invitation and request cards live
    // there, and nowhere else.
    case 'clan.invited':
    case 'clan.join.requested':
    case 'clan.join.rejected':
    case 'clan.invite.declined':
    case 'clan.member.removed':
      return '/community?tab=clans'
    // Nothing to answer — the clan itself is what you want to look at.
    case 'clan.invite.accepted':
    case 'clan.join.approved':
    case 'clan.member.left':
    case 'clan.leadership.transferred':
      return tag ? `/community/clans/${tag}` : '/community?tab=clans'
    default:
      return '/community?tab=clans'
  }
}

export function describe(n: NotificationRow): string {
  const clan = (n.data?.clanName as string) ?? 'a clan'
  switch (n.kind) {
    case 'clan.invited':
      return `${clan} invited you to join`
    case 'clan.invite.accepted':
      return `Your invitation to ${clan} was accepted`
    case 'clan.invite.declined':
      return `Your invitation to ${clan} was declined`
    case 'clan.join.requested':
      return `Somebody asked to join ${clan}`
    case 'clan.join.approved':
      return `You were accepted into ${clan}`
    case 'clan.join.rejected':
      return `Your request to join ${clan} was declined`
    case 'clan.member.left':
      return `A member left ${clan}`
    case 'clan.member.removed':
      return `You were removed from ${clan}`
    case 'clan.leadership.transferred':
      return `You now lead ${clan}`
    default:
      // An unknown kind is a row we cannot render, and saying so is better
      // than an empty line nobody can act on.
      return 'Something happened in your clan'
  }
}

/**
 * The bell.
 *
 * Renders nothing at all without a wallet session — a bell that is always
 * there but never has anything in it is furniture. The unread count is the
 * only call it makes on a page load; the list is fetched when it is opened.
 */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { account, accessToken, ready } = useSession()
  const session = { account: account as string, accessToken: accessToken as string }

  const unread = useGetUnreadCountQuery(session, {
    skip: !ready,
    pollingInterval: 60_000
  })
  const list = useGetNotificationsQuery(session, { skip: !ready || !open })
  const [markRead] = useMarkNotificationsReadMutation()
  const [markAllRead] = useMarkAllNotificationsReadMutation()

  /*
   * Shown as soon as a wallet is connected, not only once a session exists.
   *
   * The first version required both, so somebody who had connected but not yet
   * signed in saw no bell at all and had no way to know notifications were a
   * thing — the operator's own report was that this feature had not been
   * built. A control that appears only after you already know to look for it
   * is not discoverable. For a visitor with no wallet at all it stays hidden;
   * a bell that can never have anything in it is furniture.
   */
  if (!account) return null

  const count = unread.data?.unread ?? 0

  return (
    <Wrapper>
      <BellButton aria-label="Notifications" onClick={() => setOpen((v) => !v)}>
        <Bell size={18} />
        {count > 0 && <Badge>{count > 99 ? '99+' : count}</Badge>}
      </BellButton>
      {open && (
        <Panel>
          <Head>
            <Text variant={TextVariant.BODY_DEFAULT_MEDIUM}>Notifications</Text>
            {count > 0 && (
              <LinkButton onClick={() => markAllRead(session)}>Mark all read</LinkButton>
            )}
          </Head>
          {!ready && (
            <Muted variant={TextVariant.BODY_SMALL}>
              Sign in with your wallet to see your notifications.
            </Muted>
          )}
          {ready && list.isLoading && (
            <Muted variant={TextVariant.BODY_SMALL}>Loading…</Muted>
          )}
          {ready && !list.isLoading && (list.data?.notifications?.length ?? 0) === 0 && (
            <Muted variant={TextVariant.BODY_SMALL}>Nothing yet.</Muted>
          )}
          {list.data?.notifications?.map((n) => (
            <Item
              key={n._id}
              $unread={!n.readAt}
              onClick={() => {
                if (!n.readAt) markRead({ ...session, ids: [n._id] })
                setOpen(false)
                navigate(hrefFor(n))
              }}
            >
              <ItemIcon>{iconFor(n.kind)}</ItemIcon>
              <span>
                <Text variant={TextVariant.BODY_SMALL}>{describe(n)}</Text>
                <Muted variant={TextVariant.BODY_SMALL}>
                  {new Date(n.createdAt).toLocaleString()}
                </Muted>
              </span>
            </Item>
          ))}
          {/*
            The dropdown shows the last few; the register shows everything,
            including what has been read. A panel that is the only way to reach
            your history is a panel you can never close.
          */}
          <FooterLink
            onClick={() => {
              setOpen(false)
              navigate('/notifications')
            }}
          >
            See all notifications
          </FooterLink>
        </Panel>
      )}
    </Wrapper>
  )
}

export default NotificationBell
